import { resolveQuickAddProperties } from "../../cart/quick-add-properties";

const PROPERTY_ORDER = [
  "Item",
  "Fit",
  "Color",
  "_color",
  "Size",
  "_size",
  "Length",
  "_length",
  "Lapel",
  "_lapel",
  "Mesh",
  "_mesh",
  "Pant Waist",
  "_pant waist",
  "Slit",
  "_slit",
  "Delivery Time",
  "_delivery",
  "Rush Amount",
  "_stylegroup",
];

const GIFT_CARD_RECIPIENT_KEYS = ["email", "name", "message", "send_on", "recipient_email", "recipient_name"];

function sortProperties(properties) {
  const rank = (key) => {
    const index = PROPERTY_ORDER.indexOf(key.trim().toLowerCase());

    return index === -1 ? PROPERTY_ORDER.length : index;
  };

  const sortedEntries = Object.entries(properties).sort(
    (firstEntry, secondEntry) => rank(firstEntry[0]) - rank(secondEntry[0]),
  );

  return Object.fromEntries(sortedEntries);
}

function flattenErrorMessages(value) {
  if (value == null) {
    return [];
  }

  if (typeof value === "string" || typeof value === "number") {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenErrorMessages(item));
  }

  if (typeof value === "object") {
    return Object.values(value).flatMap((item) => flattenErrorMessages(item));
  }

  return [];
}

function hasGiftCardRecipientError(value) {
  if (!value || typeof value !== "object") {
    return false;
  }

  return Object.entries(value).some(([key, nestedValue]) => {
    const normalizedKey = key.trim().toLowerCase().replaceAll(" ", "_");

    return GIFT_CARD_RECIPIENT_KEYS.includes(normalizedKey) || hasGiftCardRecipientError(nestedValue);
  });
}

class AddToCart extends HTMLElement {
  constructor() {
    super();

    this.button = this.closest("button");
    this.form = this.button?.form;

    if (!this.form) return;

    this.isGiftCard = this.form.dataset.giftCard === "true";

    this.onSubmit = (event) => {
      event.preventDefault();

      const formData = new FormData(this.form);
      const allowedKeys = ["id", "quantity"];
      const data = {};

      for (const [name, value] of formData) {
        const property = /^properties\[(?<propertyKey>.+)\]$/.exec(name);

        if (!property && !allowedKeys.includes(name)) {
          continue;
        }

        if (!property) {
          data[name] = value;
          continue;
        }

        const propertyKey = property.groups.propertyKey;

        const propertyValue = typeof value === "string" ? value.trim() : value;

        if (propertyValue === "") {
          continue;
        }

        if (!data.properties) {
          data.properties = {};
        }

        data.properties[propertyKey] = propertyValue;
      }

      if (!("quantity" in data)) {
        data.quantity = 1;
      }

      data.quantity = Number.parseInt(data.quantity, 10);

      const quickAddProperties = resolveQuickAddProperties({
        color: this.button.dataset.color,
        colorCode: this.button.dataset.colorCode,
        styleGroup: this.button.dataset.styleGroup,
        deliveryOption: this.button.dataset.deliveryOption,
      });

      for (const [propertyKey, propertyValue] of Object.entries(quickAddProperties)) {
        if (!data.properties) {
          data.properties = {};
        }

        if (!(propertyKey in data.properties)) {
          data.properties[propertyKey] = propertyValue;
        }
      }

      if (!this.isGiftCard && data.properties) {
        data.properties = sortProperties(data.properties);
      }

      window.addToCart(data, {
        onError: (response) => {
          if (this.isGiftCard) {
            this.handleGiftCardError(response, formData);

            return;
          }

          this.handleProductError(response);
        },

        onAfterAdd: () => {
          document.dispatchEvent(new CustomEvent("mini-cart:open"));
        },
      });
    };

    this.form.addEventListener("submit", this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmit);
  }

  getErrorMessage(response, fallbackMessage) {
    const candidates = [response?.errors, response?.description, response?.message];

    for (const candidate of candidates) {
      const messages = flattenErrorMessages(candidate)
        .map((message) => message.trim())
        .filter(Boolean);

      if (messages.length > 0) {
        return [...new Set(messages)].join(", ");
      }
    }

    return fallbackMessage;
  }

  handleGiftCardError(response, formData) {
    const recipientErrors =
      response?.errors && typeof response.errors === "object"
        ? response.errors
        : response?.description && typeof response.description === "object"
          ? response.description
          : null;

    if (recipientErrors) {
      document.dispatchEvent(
        new CustomEvent("cart:errors", {
          detail: {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: recipientErrors,
            message: response?.message,
          },
        }),
      );

      if (hasGiftCardRecipientError(recipientErrors)) {
        return;
      }
    }

    const fallbackMessage =
      window.localeStrings?.product?.add_to_cart_error ||
      this.form.closest("product-form")?.dataset.localeAddToCartError ||
      "We weren't able to add that item to your cart.";

    this.showError(this.getErrorMessage(response, fallbackMessage));
  }

  handleProductError(response) {
    const fallbackMessage =
      this.form.closest("product-form")?.dataset.localeAddToCartError ||
      "We weren't able to add that item to your cart.";

    this.showError(this.getErrorMessage(response, fallbackMessage));
  }

  showError(message) {
    this.form.querySelector(".add-to-cart-error")?.remove();

    const error = document.createElement("div");

    error.classList.add("add-to-cart-error");

    error.setAttribute("role", "alert");

    error.textContent = typeof message === "string" ? message : "We weren't able to add that item to your cart.";

    this.form.append(error);

    window.setTimeout(() => {
      error.remove();
    }, 7000);
  }
}

if (!customElements.get("add-to-cart")) {
  customElements.define("add-to-cart", AddToCart);
}
